import React, { useEffect, useState } from "react";
import { SlidersHorizontal, Star, EyeSlash, Eye, ArrowsClockwise } from "@phosphor-icons/react";
import { baseNavItems, ADMIN_NAV_ITEM } from "@/lib/navConfig";
import { getPrefs, setPrefs, toggleFavorite, toggleHidden, applyDensity } from "@/lib/prefs";
import { useAuth } from "@/lib/AuthContext";
import { showToast } from "@/lib/toast";

/**
 * Preferences view — accent-free, function-first. Lets the user:
 *   • Pick a default landing view (the screen the app opens to)
 *   • Switch between comfortable and compact density
 *   • Pin items to a "Pinned" group at the top of the sidebar
 *   • Hide nav items they never use (e.g. Admin for non-admins doesn't show)
 *   • Reset everything back to defaults
 *
 * All prefs persist to localStorage via `lib/prefs.js`. Changes broadcast a
 * `prefschange` window event so the sidebar re-reads without a page reload.
 */
export default function PreferencesView() {
  const { user } = useAuth();
  const [, force] = useState(0);
  const prefs = getPrefs();

  const allItems = [...baseNavItems];
  if (user?.role === "admin") allItems.push(ADMIN_NAV_ITEM);

  function bump() { force((n) => n + 1); }

  function setDensity(d) {
    setPrefs({ density: d });
    applyDensity(d);
    bump();
  }

  function setDefaultView(id) {
    setPrefs({ defaultView: id });
    bump();
    showToast({ variant: "success", message: `Default view set to ${allItems.find((x) => x.id === id)?.label || id}.`, duration: 2500 });
  }

  function reset() {
    setPrefs({ favorites: [], hidden: [], defaultView: "dashboard", density: "comfortable" });
    applyDensity("comfortable");
    bump();
    showToast({ variant: "success", message: "Preferences reset to defaults.", duration: 2500 });
  }

  // Re-render this view whenever another component (e.g. sidebar star button)
  // bumps a pref so the displayed state always matches storage.
  useEffect(() => {
    const handler = () => bump();
    window.addEventListener("prefschange", handler);
    return () => window.removeEventListener("prefschange", handler);
  }, []);

  return (
    <div className="view-grid" data-testid="preferences-view">
      <div className="panel">
        <div className="panel-header">
          <SlidersHorizontal size={18} weight="bold" />
          <span>PREFERENCES</span>
        </div>
        <div className="panel-body">
          <p className="panel-desc" style={{ marginTop: 0 }}>
            Tune ClaimOps to fit how you actually work. Pin the views you use every day, hide the ones you don&apos;t, and pick which screen the app opens to. Press <kbd className="kbd-inline">⌘K</kbd> / <kbd className="kbd-inline">Ctrl-K</kbd> anywhere to jump to any view or VA form instantly.
          </p>

          {/* DENSITY */}
          <div className="pref-section" data-testid="pref-density">
            <h3 className="pref-section-title">Density</h3>
            <p className="pref-section-desc">How tightly content packs in. Compact fits more on screen; comfortable is easier on the eye.</p>
            <div className="pref-choice-row">
              {["comfortable", "compact"].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDensity(d)}
                  className={`pref-choice ${prefs.density === d ? "pref-choice--active" : ""}`}
                  data-testid={`pref-density-${d}`}
                >
                  {d.charAt(0).toUpperCase() + d.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* DEFAULT LANDING VIEW */}
          <div className="pref-section" data-testid="pref-default-view">
            <h3 className="pref-section-title">Default landing view</h3>
            <p className="pref-section-desc">The screen ClaimOps opens to after you sign in.</p>
            <select
              className="field-input"
              value={prefs.defaultView}
              onChange={(e) => setDefaultView(e.target.value)}
              data-testid="pref-default-view-select"
              style={{ maxWidth: 360 }}
            >
              {allItems.map((it) => (
                <option key={it.id} value={it.id}>{it.label}</option>
              ))}
            </select>
          </div>

          {/* SIDEBAR CUSTOMIZATION */}
          <div className="pref-section" data-testid="pref-sidebar">
            <h3 className="pref-section-title">Sidebar layout</h3>
            <p className="pref-section-desc">
              <Star size={11} weight="fill" style={{ color: "var(--gold-500)", verticalAlign: "-1px" }} /> pins an item to a &quot;Pinned&quot; group at the top of the sidebar.
              {" "}<EyeSlash size={11} weight="bold" style={{ verticalAlign: "-1px" }} /> hides it completely. You can always reach hidden views via <kbd className="kbd-inline">⌘K</kbd>.
            </p>
            <div className="pref-nav-grid">
              {allItems.map((it) => {
                const Icon = it.icon;
                const pinned = prefs.favorites.includes(it.id);
                const hidden = prefs.hidden.includes(it.id);
                return (
                  <div key={it.id} className="pref-nav-row" data-testid={`pref-nav-${it.id}`}>
                    <Icon size={15} weight="regular" />
                    <span className="pref-nav-label">{it.label}</span>
                    <button
                      type="button"
                      onClick={() => { toggleFavorite(it.id); bump(); }}
                      className={`pref-nav-toggle ${pinned ? "pref-nav-toggle--on" : ""}`}
                      data-testid={`pref-pin-${it.id}`}
                      aria-label={pinned ? "Unpin" : "Pin"}
                      title={pinned ? "Unpin from sidebar" : "Pin to sidebar"}
                    >
                      <Star size={13} weight={pinned ? "fill" : "regular"} />
                    </button>
                    <button
                      type="button"
                      onClick={() => { toggleHidden(it.id); bump(); }}
                      className={`pref-nav-toggle ${hidden ? "pref-nav-toggle--on" : ""}`}
                      data-testid={`pref-hide-${it.id}`}
                      aria-label={hidden ? "Show" : "Hide"}
                      title={hidden ? "Show in sidebar" : "Hide from sidebar"}
                    >
                      {hidden ? <EyeSlash size={13} weight="bold" /> : <Eye size={13} weight="regular" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RESET */}
          <div className="pref-section" data-testid="pref-reset-section">
            <button type="button" className="btn btn--outline btn--sm" onClick={reset} data-testid="pref-reset-btn">
              <ArrowsClockwise size={12} weight="bold" /> RESET TO DEFAULTS
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
